import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Archive as ArchiveIcon, Search, Loader2, Car, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * ArchivedJobsPage — searchable list of jobs that have been collected
 * 24h+ ago. Anything younger stays on the live workshop views.
 *
 * Steve's spec (Aug 2026):
 *   "We need to look at what we do with completed jobs how we store the
 *    data can we archive them so they come out of the app so it doesn't
 *    get clogged up."
 *
 * User picked option (b): archive after 24h and expose here under Reports.
 * Search covers reg, customer name, phone, email + a date-range filter.
 */
export default function ArchivedJobsPage() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [archiveAfterHours, setArchiveAfterHours] = useState(24);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const r = await api.get("/jobs/archived", { params });
      setRows(r.data?.jobs || []);
      setCount(r.data?.count || 0);
      setArchiveAfterHours(r.data?.archive_after_hours || 24);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load archive");
    } finally {
      setLoading(false);
    }
  }, [q, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const onSubmit = (e) => { e.preventDefault(); load(); };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/40 sticky top-0 z-30 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/dashboard" className="btn-outline" data-testid="archive-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="text-right">
            <div className="label-overline">Reports</div>
            <h1 className="font-display font-bold text-lg leading-tight flex items-center gap-2 justify-end">
              <ArchiveIcon className="w-4 h-4 text-primary" /> Archive
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6" data-testid="archive-page">
        <div className="card-default p-5">
          <p className="text-sm text-muted-foreground">
            Jobs collected more than <span className="font-mono-plex font-bold text-foreground">{archiveAfterHours}h</span> ago
            drop out of the day-to-day workshop screens and live here — searchable, but out of the way.
          </p>
          <form onSubmit={onSubmit} className="mt-4 grid grid-cols-1 sm:grid-cols-12 gap-3">
            <label className="sm:col-span-5 relative block">
              <span className="label-overline">Search</span>
              <div className="relative mt-1">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Reg, customer, phone, email"
                  className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-background text-sm"
                  data-testid="archive-search-input"
                />
              </div>
            </label>
            <label className="sm:col-span-3 block">
              <span className="label-overline">Collected from</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-sm"
                data-testid="archive-date-from"
              />
            </label>
            <label className="sm:col-span-3 block">
              <span className="label-overline">To</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background text-sm"
                data-testid="archive-date-to"
              />
            </label>
            <div className="sm:col-span-1 flex items-end">
              <button
                type="submit"
                className="w-full px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-bold hover:brightness-110 transition"
                data-testid="archive-search-btn"
              >
                Go
              </button>
            </div>
          </form>
        </div>

        <div className="flex items-center justify-between">
          <div className="label-overline" data-testid="archive-result-count">
            {loading ? "Loading…" : `${count} result${count === 1 ? "" : "s"}`}
          </div>
        </div>

        {loading ? (
          <div className="card-default p-10 text-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="card-default p-10 text-center" data-testid="archive-empty">
            <ArchiveIcon className="w-8 h-8 mx-auto text-muted-foreground/60 mb-3" />
            <p className="text-sm text-muted-foreground">
              No archived jobs match your filters yet.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {rows.map((j) => (
              <li key={j.id}>
                <Link
                  to={`/jobs/${j.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-md border border-border bg-card hover:border-primary/40 hover:bg-primary/[0.04] transition-colors group"
                  data-testid={`archive-row-${j.id}`}
                >
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-muted/50 text-muted-foreground shrink-0">
                    <Car className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm truncate">
                        {j.customer_name || "—"}
                      </span>
                      <span className="text-xs text-muted-foreground font-mono-plex">
                        {j.vehicle_reg}
                      </span>
                      {j.vehicle_make && (
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          {j.vehicle_make} {j.vehicle_model || ""}
                        </span>
                      )}
                      {j.csi_score != null && (
                        <span
                          className={`text-[10px] uppercase tracking-wider font-mono-plex font-bold ${
                            j.csi_score >= 9 ? "text-emerald-500" : j.csi_score >= 7 ? "text-warning" : "text-destructive"
                          }`}
                          data-testid={`archive-csi-${j.id}`}
                        >
                          CSI {j.csi_score}/10
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      Collected {formatDate(j.completed_at)}
                      {j.booked_service_name ? ` · ${j.booked_service_name}` : j.work_description ? ` · ${truncate(j.work_description, 40)}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {(j.approved_total || j.booked_service_amount) > 0 && (
                      <div className="text-sm font-mono-plex font-bold">
                        £{(Number(j.approved_total || 0) + Number(j.booked_service_amount || 0)).toFixed(2)}
                      </div>
                    )}
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors ml-auto" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
           " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
